// GroupChat.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import backend from "../../../../../api";
import AppHeader from "../../../../../components/AppHeader";
import "./GroupChat.css";
import { getSocket, connectSocket, safeEmit } from "../../../../../api/socket";
import { useSelector } from "react-redux";

function MessageItem({ item, mine }) {
  return (
    <div className={`gc-bubble ${mine ? "gc-mine" : "gc-theirs"}`}>
      {!mine && item.senderId?.name && <div className="gc-sender">{item.senderId.name}</div>}
      {item.text && <div className="gc-text">{item.text}</div>}
      {item.attachments?.length > 0 && (
        <ul className="gc-files">
          {item.attachments.map((a,i)=>(
            <li key={i}><a href={a.url} target="_blank" rel="noreferrer">{a.name || "file"}</a></li>
          ))}
        </ul>
      )}
      <div className="gc-meta">{new Date(item.createdAt).toLocaleTimeString()}</div>
    </div>
  );
}

export default function GroupChat() {
  const { groupId } = useParams();
  const nav = useNavigate();
  const { user } = useSelector(s => s.auth);
  const userId = user?._id;

  const [group, setGroup] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [locationRequester, setLocationRequester] = useState("");
  const listRef = useRef(null);
  const notifRef = useRef(null);

  const keyId = (m, i) => String(m?._id ?? `${m.senderId}_${m.createdAt}_${i}`);

  const load = async () => {
    try {
      setErr("");
      const gRes = await backend.get(`/groups/${groupId}`);
      setGroup(gRes?.data?.data || null);
      const res = await backend.get(`/messages/group/${groupId}`, { params: { userId } });
      setMsgs(res?.data?.data || []);
      setTimeout(()=> listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load chat");
    }
  };

  useEffect(() => {
    if (userId) {
      connectSocket(userId);
    }
    load();
    const socket = getSocket();
    if (socket) {
      socket.emit("join-room", { roomId: `group:${groupId}` });
      const onNew = (m) => {
        if (String(m.groupId) === String(groupId)) {
          setMsgs(prev => [...prev, m]);
          setTimeout(()=> listRef.current?.scrollTo(0, listRef.current.scrollHeight), 10);
        }
      };
      const onUpdated = (data) => {
        if (data.groupId === groupId) {
          setGroup(prev => ({ ...prev, name: data.name }));
        }
      };
      socket.on("message:new", onNew);
      socket.on("group:updated", onUpdated);
      return () => {
        socket.off("message:new", onNew);
        socket.off("group:updated", onUpdated);
        socket.emit("leave-room", { roomId: `group:${groupId}` });
      };
    }
  }, [groupId, userId]);

  // useEffect(() => {
  //   const socket = getSocket();
  //   if (socket) {
  //     const onCallOffer = (data) => {
  //       if (data.to !== userId) return;
  //       if (window.confirm(`Incoming call from ${data.from}. Accept?`)) {
  //         localStorage.setItem('incomingCall', JSON.stringify(data));
  //         nav(`/call/${data.from}`);
  //       } else {
  //         socket.emit('call:decline', { to: data.from });
  //       }
  //     };
  //     socket.on('call:offer', onCallOffer);
  //     return () => socket.off('call:offer', onCallOffer);
  //   }
  // }, [userId, nav]);

  useEffect(() => {
    const socket = getSocket();
    if (socket) {
      const onLocationRequest = (data) => {
        if (data.to !== userId) return;
        setLocationRequester(data.fromName);
        setShowLocationPopup(true);
        if (notifRef.current) notifRef.current.play();
      };
      socket.on('location:request', onLocationRequest);
      return () => socket.off('location:request', onLocationRequest);
    }
  }, [userId]);

  const acceptLocation = () => {
    if (notifRef.current) notifRef.current.pause();
    setShowLocationPopup(false);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        try {
          await backend.post('/location', { lat, lng, accuracy }, { params: { userId } });
        } catch (e) {
          alert('Failed to share location');
        }
      },
      (err) => alert('Location access denied'),
      { enableHighAccuracy: true }
    );
  };

  const declineLocation = () => {
    if (notifRef.current) notifRef.current.pause();
    setShowLocationPopup(false);
  };

  const send = async () => {
    if (!text.trim()) return;
    try {
      await backend.post(`/messages/group`, { groupId, text }, { params: { userId } });
      setText("");
    } catch (e) {
      setErr(e?.response?.data?.message || "Send failed");
    }
  };

  return (
    <div className="gc-page">
      <AppHeader
        title={group?.name || "Group Chat"}
        onBack={() => nav(-1)}
        right={
          <>
            <button className="btn-light" onClick={()=> nav(`/groups/${groupId}/edit`)}>Settings</button>
            <button className="btn-light" onClick={()=> nav(`/groups/${groupId}/map`)}>Map</button>
          </>
        }
      />
      {err && <div className="gc-error">{err}</div>}
      <div className="gc-list" ref={listRef}>
        {msgs.map((m,i)=>(
          <MessageItem key={keyId(m,i)} item={m} mine={String(m.senderId?._id || m.senderId) === String(userId)} />
        ))}
      </div>
      <div className="gc-composer">
        <input className="gc-input" placeholder="Write your message" value={text} onChange={e=>setText(e.target.value)} onKeyDown={(e)=> e.key==="Enter" && send()} />
        <button className="gc-send" onClick={send}>➤</button>
      </div>
      <audio ref={notifRef} src="https://freesound.org/data/previews/66/66176_634166-lq.mp3" />
      {showLocationPopup && (
        <div className="modal">
          <div className="modal-content">
            <h3>{locationRequester} requests your location. Share?</h3>
            <button className="btn-brand" onClick={acceptLocation}>Yes</button>
            <button className="btn-light" onClick={declineLocation}>No</button>
          </div>
        </div>
      )}
    </div>
  );
}